import Axios from 'axios';

/**
 * Inspection API client — `/bp/inspection/…` per unit_count_operations_spec.md §5.
 * Every call carries the acting person's JWT; station calls add `X-Station`.
 * Mock mode (`?mock=1` or localStorage.inspectionMock='1') serves fixtures from ./mock.js —
 * DEV builds only (system-admin F26 / production-supervisor F18): a production build never reads
 * the flag, never persists it and `isMock()` is always false there. The fixture module is loaded
 * with a dynamic import behind `import.meta.env.DEV`, so it is not in a production bundle (EXV-047).
 * Mock mode is a developer convenience and must never feed an ACL decision (see DefectCodes.jsx).
 */
export const BASE = '/bp/inspection';

/** The fixture backend, DEV builds only: `import.meta.env.DEV` is false at build time, so the import is dropped. */
const mockRequest = async (...args) => {
  if (!import.meta.env.DEV) throw new Error('mock mode is not available in this build');
  const m = await import('./mock');
  return m.mockRequest(...args);
};

export const isMock = () => {
  // `import.meta.env.DEV` (not a variable) so a production build folds this to `return false` and drops the rest
  if (!import.meta.env.DEV) return false;
  try {
    const q = new URLSearchParams(window.location.search).get('mock');
    if (q === '1') localStorage.setItem('inspectionMock', '1');
    if (q === '0') localStorage.removeItem('inspectionMock');
    return localStorage.getItem('inspectionMock') === '1';
  } catch (e) { return false; }
};
export const setMock = (on) => {
  if (!import.meta.env.DEV) return;
  try { if (on) localStorage.setItem('inspectionMock', '1'); else localStorage.removeItem('inspectionMock'); } catch (e) { /* ignore */ }
};

export const getStationCode = () => { try { return localStorage.getItem('station_code') || ''; } catch (e) { return ''; } };
export const setStationCode = (code) => { try { if (code) localStorage.setItem('station_code', code); else localStorage.removeItem('station_code'); } catch (e) { /* ignore */ } };

/** Low-level request. Resolves to the response body `{status, data}`; rejects with the axios error. */
export async function api(method, path, { params, data, station } = {}) {
  if (isMock()) return mockRequest(method, path, { params, data, station: station || getStationCode() });
  const headers = { Authorization: `Bearer ${localStorage.getItem('Token')}` };
  const st = station || getStationCode();
  if (st) headers['X-Station'] = st;
  const r = await Axios({ method, url: `${BASE}/${path}`, params, data, headers });
  return r?.data;
}
export const get = (path, params, opts) => api('get', path, { params, ...(opts || {}) });
export const post = (path, data, opts) => api('post', path, { data, ...(opts || {}) });
export const put = (path, data, opts) => api('put', path, { data, ...(opts || {}) });

export const errMsg = (e, fallback = 'Request failed') => e?.response?.data?.message || e?.response?.data?.detail || (e?.response ? `${fallback} (${e.response.status})` : e?.message === 'Network Error' ? 'Offline — network unavailable' : e?.message) || fallback;
export const errStatus = (e) => e?.response?.status;
export const isOfflineError = (e) => !e?.response && (e?.code === 'ERR_NETWORK' || e?.message === 'Network Error' || !navigator.onLine);

/** Other (non-inspection) endpoints the screens need: batch / operation pickers. */
export async function listActiveBatches() {
  if (isMock()) return mockRequest('get', '__batches__', {});
  const r = await Axios.get('/bp/bp_get_batch_process_active', { headers: { Authorization: `Bearer ${localStorage.getItem('Token')}` } });
  return r?.data;
}

/** Behaviour of an operation row from any of the batch payloads (string type name today; object with `behaviour` per §1). */
export const opBehaviour = (r) => {
  if (!r) return 'STANDARD';
  const b = r.behaviour || r.operation_type?.behaviour || r.operation_type_behaviour;
  if (b) return String(b).toUpperCase();
  const cfg = r.unit_count_config || r.operation_type?.unit_count_config;
  if (cfg && typeof cfg === 'object') return cfg.lot_size_tubs ? 'FILLING_LOTS' : 'UNIT_COUNT';
  return 'STANDARD';
};

/* INSP-01 D5: defect categories belong to each defect set — the board, its CSV and the editors take the batch's or the
 * set's own words from the server; the client keeps no category vocabulary. */
export const CRIT = { CRITICAL: { color: '#dc2626', bg: 'rgba(220,38,38,.10)', label: 'Critical' }, MAJOR: { color: '#d97706', bg: 'rgba(217,119,6,.12)', label: 'Major' }, MINOR: { color: '#2563eb', bg: 'rgba(37,99,235,.10)', label: 'Minor' } };
export const STATE_COLOR = { FREE: 'default', TAKEN: 'processing', DONE: 'success', HELD: 'error' };
/* A tub's status at one counting stage, as the board words it (`tubs/` -> `stages[].status`). */
export const STAGE_STATUS = { PENDING: { word: 'Pending', color: 'default' }, TAKEN: { word: 'In hand', color: 'processing' }, DONE: { word: 'Done', color: 'success' }, HELD: { word: 'Held', color: 'error' } };
export const STAGE_LABEL = { STOPPER_POSITION: 'Stopper position', VI_FIRST: '1st visual', VI_SECOND: '2nd visual', RE_INSPECTION: 'Re-inspection', AQL: 'AQL', LABELLING: 'Labelling', CARTONING: 'Cartoning', SHIPPER: 'Shipper' };
export const stageLabel = (c) => STAGE_LABEL[c] || (c || '').replace(/_/g, ' ');
export const pct = (a, b) => (b ? `${((100 * a) / b).toFixed(2)} %` : '—');

/** The board's batch picker for the caller's plant (`GET board/batches/?page=`): one page —
 * `{has_inspection, batches, page, page_size, total, options, settings}`; the page size is the plant's setting; `filters` are
 * product, dosage_form, record, building, area, status, date_from, date_to; `options` are the values in the caller's scope. */
export const fetchBoardBatches = (page = 1, filters = {}) => get('board/batches/', { page, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) });
